<template>
  <div
    :class="containerClasses"
    data-ui="hero"
  >
    <div class="hero__image-frame">
      <img
        class="hero__image-lockup"
        :srcset="lockupSrcset"
        alt="R E I co-op membership, made for life outside"
      >
      <img
        class="hero__image-stamp"
        :srcset="stampSrcset"
        sizes="
          (min-width: 992px) 400px,
          130px
        "
        alt=""
        aria-hidden="true"
      >
      <div
        v-if="img?.attribution"
        class="hero__image-attribution"
      >
        <icon-camera sise="small" />
        <cdr-text tag="span">
          {{ img.attribution }}
        </cdr-text>
      </div>
      <picture class="hero__image">
        <source
          media="(-webkit-min-device-pixel-ratio: 1.5)"
          :srcset="bgImgSrcset"
          sizes="100vw"
        >
        <img
          :srcset="bgImgSrcsetFallback"
          :alt="img?.alt"
          sizes="100vw"
        >
      </picture>
    </div>
    <countdown-banner
      v-if="showCountdownBanner && showCountdownBannerWithinHero"
      v-bind="countdownBannerModel"
    />
    <div
      :class="contentWrapperClasses"
      :data-ui="contentDataUi"
    >
      <div
        v-if="!defaultSlotIsEmpty"
        class="hero__slot-content"
        data-ui="hero-slot"
      >
        <slot />
      </div>
      <slot name="inset-box" />
    </div>
    <countdown-banner
      v-if="showCountdownBanner && !showCountdownBannerWithinHero"
      v-bind="countdownBannerModel"
    />
  </div>
</template>

<script setup>
import { computed, inject, useSlots } from 'vue';
import { CdrText, IconCamera } from '@rei/cedar';
import CountdownBanner from '../countdown-banner/CountdownBanner.vue';

import SetupEmptySlotCheck from '../../../utils/slot-is-empty';

const userInformation = inject('userInformation');

const props = defineProps({
  img: { type: Object, required: true },
  loading: { type: Boolean, required: true },
  loginUrl: { type: String, default: '' },
  countdownBannerModel: { type: Object, default: null },
  screenWidth: { type: Number, default: 375 },
  showCountdownBanner: { type: Boolean, default: false },
});

const isMember = computed(() => userInformation?.user?.isMember);
const slots = useSlots();
const defaultSlotIsEmpty = SetupEmptySlotCheck({ slot: slots.default });

const src = computed(() => props?.img?.src);

const containerClasses = computed(() => (`hero hero--slot-${defaultSlotIsEmpty.value ? 'in' : ''}active`));
const showCountdownBannerWithinHero = computed(() => {
  const { screenWidth } = props;
  const { value: slotIsEmpty } = defaultSlotIsEmpty;
  // CdrBreakpointLg
  return slotIsEmpty || (screenWidth <= 1232);
});

const bgImgSrcset = computed(() => `
  ${src.value}?im=Resize,width=850 850w,
  ${src.value}?im=Resize,width=1280 1280w,
  ${src.value}?im=Resize,width=2000 2000w,
  ${src.value}?im=Resize,width=3500 3500w,
`);
const bgImgSrcsetFallback = computed(() => `
  ${src.value}?im=Resize,width=850&density=1 850w,
  ${src.value}?im=Resize,width=1600&density=1 1600w,
  ${src.value}?im=Resize,width=2200&density=1 2200w,
`);
// eslint-disable-next-line
const lockupSrcset = computed(() => {
  return defaultSlotIsEmpty.value
    ? 'https://www.rei.com/dam/21038114_membership-landing-page-hero-lockup-lg.png'
    : 'https://www.rei.com/dam/21029044_membership-landing-page-hero-lockup.png';
});

const stampSrcset = computed(() => {
  const base = 'https://www.rei.com/dam/21028995_-mem_membership_symbol_main_yellow.png';
  return `
    ${base}?im=Resize,width=500 500w,
    ${base}?im=Resize,width=700 700w,
  `;
});

const showPersonalizedContent = computed(() => !!(!props.loading && isMember.value));

const contentWrapperClasses = computed(() => ({
  'hero__content-wrapper': true,
  'hero__content-wrapper--is-member': showPersonalizedContent.value,
}));
const contentDataUi = computed(() => {
  const str = showPersonalizedContent.value ? '' : '-non';
  return `hero-content-wrapper${str}-member`;
});
</script>

<style lang="scss">
@import '../../../../style/common/paths.scss';

$hero-max-width: 1400px;

.hero {
  position: relative;
  margin-bottom: $cdr-space-one-and-a-half-x;
}

.hero__image-frame {
  position: relative;
  background-color: $cdr-color-background-brand-spruce;
  overflow: hidden;
}

.hero__image img {
  height: 100%;
  width: 100%;
  object-fit: cover;
  object-position: cover;
}

.hero__image-attribution {
  position: absolute;
  display: flex;
  align-items: center;
  gap: $cdr-space-three-eighth-x;
  bottom: $cdr-space-one-and-a-half-x;
  left: $cdr-space-one-x;
  svg {
    fill: $cdr-color-text-inverse;
    width: 11px;
  }
  span {
    @include cdr-text-utility-sans-strong-100;
    color: $cdr-color-text-inverse;
    font-size: 1rem;
    text-shadow:
      $cdr-prominence-raised-x
      $cdr-prominence-raised-y
      $cdr-prominence-raised-blur
      rgba(46, 46, 43, 0.8);
    @include cdr-md-mq-up {
      @include cdr-text-utility-sans-strong-200;
    }
  }
}

.hero__image-frame,
.hero__slot-content {
  height: 212px;
  @include cdr-md-mq-up {
    height: 320px;
  }
  @include cdr-lg-mq-up {
    height: 418px;
  }
}

// Arbitrary non-Cedar values used to position membership diamond per MEM-3061
.hero__image-stamp {
  position: absolute;
  right: -15px;
  bottom: -28px;
  max-width: 118px;
  @include cdr-md-mq-up {
    max-width: 211px;
    right: -44px;
    bottom: -56px;
  }
  @media screen and (min-width: 1500px) {
    right: -38px;
    bottom: -73px;
    max-width: 322px;
  }
}

.hero__image-lockup {
  position: absolute;
  max-width: 370px;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  margin: auto;
  @include cdr-md-mq-up {
    max-width: 480px;
  }
  @include cdr-lg-mq-up {
    max-width: 720px;
  }
}

.hero--slot-inactive {
  .hero__image-stamp {
    display: none;
    @include cdr-lg-mq-up {
      display: block;
    }
  }
}

.hero--slot-active {
  $hero-content-height-sm: 300px;
  $hero-image-height-sm: 548px;
  $hero-content-height-lg: 330px;
  $hero-image-height-lg: 740px;

  .hero__image-lockup {
    top: $cdr-space-one-and-a-half-x;
    bottom: auto;
    max-width: 320px;
    @include cdr-lg-mq-up {
      top: $cdr-space-two-x;
      max-width: 460px;
    }
  }

  .hero__image-frame {
    height: $hero-image-height-sm;
    @include cdr-lg-mq-up {
      height: $hero-image-height-lg;
    }
  }

  .hero__slot-content,
  .inset-box {
    @include cdr-lg-mq-up {
      height: $hero-content-height-lg;
      margin: 0;
      transform: translateY(4.2rem);
    }
  }

  .inset-box {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    @include cdr-lg-mq-up {
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
  }

  .hero__slot-content {
    position: absolute;
    top: 0;
    right: $cdr-space-one-and-a-half-x;
    left: $cdr-space-one-and-a-half-x;
    display: flex;
    height: initial;
    padding-top: 184px;
    @include cdr-lg-mq-up {
      position: initial;
      padding-top: initial;
    }
  }

  .inset-box__personalized-cta {
    @include cdr-md-mq-up {
      max-width: initial;
    }
  }

  .year-in-review-carousel {
    border-radius: $cdr-radius-softer;
    height: $hero-content-height-sm;
    @include cdr-lg-mq-up {
      height: $hero-content-height-lg;
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
  }

  .hero__content-wrapper {
    @include cdr-lg-mq-up {
      justify-content: center;
      align-items: center;
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      margin: auto;
      display: flex;
      padding: 0 $cdr-space-two-x;
      max-width: $hero-max-width;
    }
  }
}
</style>
